import { useEffect, useRef, useCallback } from 'react';
import { turnOffDbInstance } from '../../api/Admin/turnOnDbInstance';

const INACTIVITY_TIMEOUT = 5 * 60 * 1000; // 30 minutes in milliseconds

export const useActivityTracker = (isAuthenticated: boolean) => {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isDbActiveRef = useRef<boolean>(false);

  const resetTimer = useCallback(() => {
    // Clear existing timer
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Only set new timer if user is authenticated and database should be active
    if (isAuthenticated) {
      isDbActiveRef.current = true;
      timeoutRef.current = setTimeout(async () => {
        console.log('30 minutes of inactivity detected, turning off database...');
        try {
          await turnOffDbInstance();
          isDbActiveRef.current = false;
          console.log('Database turned off due to inactivity');
        } catch (error) {
          console.error('Failed to turn off database due to inactivity:', error);
        }
      }, INACTIVITY_TIMEOUT);
    }
  }, [isAuthenticated]);

  const handleActivity = useCallback(() => {
    resetTimer();
  }, [resetTimer]);

  useEffect(() => {
    if (!isAuthenticated) {
      // Clear timer when user logs out
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      isDbActiveRef.current = false;
      return;
    }

    // Activity events to monitor
    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click',
      'focus',
      'blur'
    ];

    // Add event listeners
    events.forEach(event => {
      document.addEventListener(event, handleActivity, true);
    });

    // Start the initial timer
    resetTimer();

    // Cleanup function
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleActivity, true);
      });
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [isAuthenticated, handleActivity, resetTimer]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return {
    isDbActive: isDbActiveRef.current,
    resetTimer: handleActivity
  };
}; 